import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { AreaSelect } from "@/components/symin/AreaSelect";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Waveform, ArrowSquareOut, Info, Warning, CircleNotch, UploadSimple } from "@phosphor-icons/react";

const GROUPS = [
  {
    group: "Structure", accent: "#38BDF8", layers: [
      { key: "structural", label: "Structural Lineaments", desc: "Interpreted faults and fractures extracted from multi-sensor imagery - the first-order structural architecture that focuses mineralising fluids." },
      { key: "density", label: "Lineament Density", desc: "Fracture-intensity surface highlighting structurally complex corridors (dilational jogs and intersections) analogous to a structural-complexity geophysical proxy." },
      { key: "sar", label: "SAR Structure", desc: "Radar backscatter structural enhancement - an all-weather, cloud-independent structural response comparable to a radar-geophysical dataset." },
    ],
  },
  {
    group: "Terrain / Potential-Field Proxy", accent: "#10B981", layers: [
      { key: "hillshade", label: "Hillshade Relief", desc: "Shaded relief emphasising subtle topographic lineaments and basement fabric, a common structural-geophysics interpretation aid." },
      { key: "slope", label: "Slope", desc: "Terrain slope from the DEM, indicating structural breaks and outcrop favourability." },
      { key: "dem", label: "Elevation (DEM)", desc: "Copernicus GLO-30 elevation model - the base surface for terrain-derived structural products." },
    ],
  },
  {
    group: "Imagery", accent: "#00B4D8", layers: [
      { key: "base", label: "Satellite Base", desc: "True-colour Sentinel-2 base mosaic of the licence for spatial reference." },
    ],
  },
];
const LAYER_META = Object.fromEntries(GROUPS.flatMap((g) => g.layers.map((l) => [l.key, { ...l, accent: g.accent }])));

export default function Geophysics() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [area, setArea] = useState(null);
  const [layer, setLayer] = useState("structural");
  const [img, setImg] = useState(null);
  const [state, setState] = useState("idle");
  const cache = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/projects/${id}/geophysics`);
        setOv(r.data);
        const first = (r.data.areas || []).find((a) => a.status === "complete") || (r.data.areas || [])[0];
        if (first) setArea(first.area_id);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [id]);

  const activeArea = useMemo(() => (ov?.areas || []).find((a) => a.area_id === area), [ov, area]);

  useEffect(() => {
    if (!area || !layer) return;
    if (activeArea && activeArea.status !== "complete") { setState("pending"); setImg(null); return; }
    const ck = `${area}:${layer}`;
    if (cache.current[ck]) { setImg(cache.current[ck]); setState("ready"); return; }
    let cancelled = false;
    setState("loading"); setImg(null);
    api.get(`/projects/${id}/areas/${area}/map/${layer}?inline=1`)
      .then((r) => { if (cancelled) return; cache.current[ck] = r.data.uri; setImg(r.data.uri); setState("ready"); })
      .catch((e) => { if (cancelled) return; setState("error"); if (e?.response?.status !== 404) toast.error(apiError(e)); });
    return () => { cancelled = true; };
  }, [id, area, layer, activeArea]);

  const meta = LAYER_META[layer] || {};
  const airborne = ov?.airborne || { count: 0, methods: [] };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="geophys" />
      <div className="ml-[64px] flex flex-col h-screen">
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <Waveform size={18} style={{ color: "#00B4D8" }} className="shrink-0" />
          <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate min-w-0" data-testid="geophysics-title">Geophysics</h1>
          <span className="text-xs text-muted-foreground truncate hidden lg:block">{ov?.project_name}</span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <AreaSelect areas={ov?.areas || []} value={area} onChange={setArea} accent="#00B4D8" testid="geophysics-area-select" />
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="geophysics-back">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
          <aside className="w-full lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-border bg-card overflow-y-auto max-h-[34vh] lg:max-h-none" data-testid="geophysics-panel">
            {/* Airborne status */}
            <div className="px-3 py-3 border-b border-border" data-testid="geophysics-airborne">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Airborne Geophysics</div>
              {airborne.count > 0 ? (
                <div className="text-xs text-foreground">{airborne.count} dataset(s){airborne.processed ? " · processed" : ""}<div className="text-muted-foreground mt-0.5">{airborne.methods.join(", ")}</div></div>
              ) : (
                <div className="text-[11px] text-muted-foreground leading-relaxed flex gap-1.5"><UploadSimple size={14} className="shrink-0 mt-0.5" style={{ color: "#00B4D8" }} />No airborne magnetics / radiometrics uploaded. Showing satellite-derived structural-geophysics layers below.</div>
              )}
            </div>
            {GROUPS.map((g) => (
              <div key={g.group} className="px-3 py-3 border-b border-border">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: g.accent }} />{g.group}
                </div>
                <div className="space-y-0.5">
                  {g.layers.map((l) => (
                    <button key={l.key} data-testid={`geophysics-layer-${l.key}`} onClick={() => setLayer(l.key)}
                      className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors ${layer === l.key ? "text-foreground" : "text-muted-foreground hover:bg-accent/50"}`}
                      style={layer === l.key ? { background: `${g.accent}1F`, boxShadow: `inset 2px 0 0 ${g.accent}` } : undefined}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          <div className="flex-1 min-w-0 bg-[#04070B] flex flex-col">
            <div className="flex-1 min-h-0 grid place-items-center p-4 relative" data-testid="geophysics-canvas">
              {state === "loading" && <div className="flex flex-col items-center gap-3 text-muted-foreground"><CircleNotch size={28} className="animate-spin" style={{ color: meta.accent }} /><span className="text-sm">Rendering {meta.label}…</span></div>}
              {state === "pending" && <div className="flex flex-col items-center gap-2 text-muted-foreground max-w-sm text-center"><Warning size={26} style={{ color: "#FBBF24" }} /><span className="text-sm">This licence area has not been processed yet. Process it in Data &amp; Setup to view its structural-geophysics layers.</span></div>}
              {state === "error" && <div className="flex flex-col items-center gap-2 text-muted-foreground max-w-sm text-center"><Warning size={26} style={{ color: "#F43F5E" }} /><span className="text-sm">This layer is not available for the selected area yet.</span></div>}
              {state === "ready" && img && <img src={img} alt={meta.label} data-testid="geophysics-image" className="max-h-full max-w-full object-contain rounded-md border border-border shadow-2xl" />}
            </div>
            <div className="shrink-0 border-t border-border bg-card px-5 py-3 flex items-start gap-3" data-testid="geophysics-layer-desc">
              <Info size={16} style={{ color: meta.accent }} className="shrink-0 mt-0.5" />
              <div>
                <div className="flex items-center gap-2"><span className="font-display font-semibold text-sm">{meta.label}</span><Badge variant="outline" className="text-[9px]">{activeArea?.name}</Badge></div>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5 max-w-3xl">{meta.desc}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
